const ROTATOR_SELECTOR = '.hero-rotator';
const SLIDE_SELECTOR   = '.hero-slide';
const DOTS_SELECTOR    = '.hero-rotator-dots';
const DOT_SELECTOR     = '.hero-dot';
const ACTIVE_CLASS     = 'is-active';
const LEAVING_CLASS    = 'is-leaving';

const INTERVAL_MS   = 6000;  // dwell per slide — comfortable for 2-3 lines of copy
const TRANSITION_MS = 600;   // matches the CSS transition duration

/**
 * Auto-advancing hero headline carousel.
 *
 * - Rotates `.hero-slide` siblings within `.hero-rotator` every INTERVAL_MS.
 * - Each transition slides the outgoing slide off to the left while the
 *   incoming slide slides in from the right (state managed via .is-active /
 *   .is-leaving classes; visual is all CSS).
 * - Pauses on hover anywhere over the rotator (so a reader who's interested
 *   isn't rushed).
 * - Pauses when the rotator scrolls off-screen (IntersectionObserver) so no
 *   timer fires uselessly.
 * - Pagination dots click to jump; pressing a dot resets the timer.
 *
 * No-op if the rotator isn't on the page (e.g. stub pages).
 */
export function initHeroCarousel() {
  const rotator = document.querySelector(ROTATOR_SELECTOR);
  if (!rotator) return;

  const slides = Array.from(rotator.querySelectorAll(SLIDE_SELECTOR));
  if (slides.length < 2) return;

  const dotsContainer = document.querySelector(DOTS_SELECTOR);
  const dots = dotsContainer ? Array.from(dotsContainer.querySelectorAll(DOT_SELECTOR)) : [];

  let activeIndex = slides.findIndex((s) => s.classList.contains(ACTIVE_CLASS));
  if (activeIndex === -1) {
    activeIndex = 0;
    slides[0].classList.add(ACTIVE_CLASS);
  }

  let paused = false;       // true while the cursor is over the rotator
  let visible = true;       // true while the rotator is in the viewport
  let timer = null;         // interval handle when running, null when stopped
  let leavingTimer = null;  // timeout that clears .is-leaving after the CSS transition

  function goTo(targetIndex) {
    const total = slides.length;
    const next = ((targetIndex % total) + total) % total;
    if (next === activeIndex) return;

    const oldSlide = slides[activeIndex];
    const newSlide = slides[next];

    oldSlide.classList.add(LEAVING_CLASS);
    oldSlide.classList.remove(ACTIVE_CLASS);
    newSlide.classList.add(ACTIVE_CLASS);

    if (dots[activeIndex]) {
      dots[activeIndex].classList.remove(ACTIVE_CLASS);
      dots[activeIndex].setAttribute('aria-selected', 'false');
    }
    if (dots[next]) {
      dots[next].classList.add(ACTIVE_CLASS);
      dots[next].setAttribute('aria-selected', 'true');
    }

    activeIndex = next;

    clearTimeout(leavingTimer);
    leavingTimer = setTimeout(() => {
      oldSlide.classList.remove(LEAVING_CLASS);
    }, TRANSITION_MS);
  }

  function start() {
    if (timer !== null || paused || !visible) return;
    timer = setInterval(() => goTo(activeIndex + 1), INTERVAL_MS);
  }

  function stop() {
    if (timer === null) return;
    clearInterval(timer);
    timer = null;
  }

  // Pause on hover (reader-friendly).
  rotator.addEventListener('mouseenter', () => { paused = true;  stop(); });
  rotator.addEventListener('mouseleave', () => { paused = false; start(); });

  // Pause when not visible.
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      visible = entries[0].isIntersecting;
      visible ? start() : stop();
    }, { threshold: 0.1 });
    io.observe(rotator);
  }

  // Pagination dots: jump + reset the auto-advance timer so the user gets a
  // fresh full interval to read the slide they chose.
  dots.forEach((dot, i) => {
    dot.addEventListener('click', () => {
      goTo(i);
      stop();
      start();
    });
  });

  start();
}
